import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { signIn, userData } = useAuth();

  // Efecto para redirigir cuando el login sea exitoso y userData esté disponible
  useEffect(() => {
    if (loginSuccess && userData) {
      // Redirigir según el rol del usuario
      switch (userData.role) {
        case 'worker':
          navigate('/worker-dashboard');
          break;
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'business':
          navigate('/business/dashboard');
          break;
        default:
          // Si no hay rol definido, redirigir al worker dashboard por defecto
          navigate('/worker-dashboard');
      }
      setLoginSuccess(false); // Resetear el estado
    }
  }, [loginSuccess, userData, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      setLoginSuccess(true); // Marcar que el login fue exitoso
    } catch (error: any) {
      console.error('Error during login:', error);
      setError(
        error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password'
          ? t('login.error')
          : t('login.error')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex justify-center items-center animate-fadeIn">
      <div className="bg-white/95 p-8 rounded-2xl shadow-2xl w-full max-w-md relative z-10 backdrop-blur-md border border-white/20 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl">
        {/* Logo con animación */}
        <div className="flex justify-center mb-8">
          <div className="relative group">
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54" 
              alt="Brix Logo" 
              className="w-32 max-h-20 object-contain drop-shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </div>
        </div>

        {/* Título con efecto de gradiente */}
        <h2 className="mb-8 text-3xl text-center font-bold bg-gradient-to-r from-gray-800 via-red-600 to-gray-800 bg-clip-text text-transparent animate-pulse">
          {t('login.title')}
        </h2>

        {/* Mensaje de error con animación */}
        {error && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg animate-shake">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campo de email */}
          <div className="relative group">
            <label htmlFor="email" className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaEnvelope className="text-red-500" /> {t('login.email')}
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder={t('login.emailPlaceholder')}
              />
              <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
            </div>
          </div>

          {/* Campo de contraseña */}
          <div className="relative group">
            <label htmlFor="password" className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
              <FaLock className="text-red-500" /> {t('login.password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full p-4 pl-12 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300"
                placeholder={t('login.passwordPlaceholder')}
              />
              <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Botón de envío */}
          <button 
            type="submit" 
            className="w-full p-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold text-lg transition-all duration-300 relative overflow-hidden hover:from-red-600 hover:to-red-700 hover:-translate-y-1 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none group"
            disabled={loading}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <FaLock />
                  {t('login.submit')}
                </>
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          </button>
        </form>

        {/* Enlaces de navegación */}
        <div className="flex flex-col items-center gap-4 mt-8 pt-6 border-t border-gray-200">
          <Link 
            to="/auth/register" 
            className="text-red-500 hover:text-red-600 text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-1 group"
          >
            <span className="group-hover:underline">{t('login.noAccount')}</span>
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <Link 
            to="/auth/forgot-password" 
            className="text-blue-500 hover:text-blue-600 text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-1 group"
          >
            <span className="group-hover:underline">{t('login.forgotPassword')}</span>
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}; 